import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tag } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

export const couponDiscount = (coupon, base) => {
  if (!coupon || !base || base < coupon.min_amount) return 0;
  if (coupon.type === 'flat') return coupon.discount;
  return Math.min(Math.floor((base * coupon.discount) / 100), coupon.max_discount ?? base);
};

export const CouponSection = ({ coupons, baseAmount, applied, onApply, onRemove }) => {
  const [code, setCode] = useState('');
  const [showInput, setShowInput] = useState(false);

  const tryApply = (coupon) => {
    if (!coupon) {
      toast({ title: 'Invalid Coupon', description: 'Please enter a valid coupon code', variant: 'destructive' });
      return;
    }
    if (!baseAmount) {
      toast({ title: 'Select Hours First', description: 'Choose the number of hours before applying a coupon', variant: 'destructive' });
      return;
    }
    if (baseAmount < coupon.min_amount) {
      toast({ title: 'Minimum Amount Not Met', description: `Minimum booking amount of ₹${coupon.min_amount} required`, variant: 'destructive' });
      return;
    }
    onApply(coupon);
    toast({ title: 'Coupon Applied!', description: coupon.description });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-base">Have a Coupon?</Label>
        {!showInput && !applied && (
          <Button variant="link" size="sm" className="text-primary" onClick={() => setShowInput(true)} data-testid="show-coupon-btn">
            <Tag className="w-4 h-4 mr-1" />Apply Coupon
          </Button>
        )}
      </div>
      {showInput && !applied && (
        <>
          <div className="flex gap-2">
            <Input placeholder="Enter coupon code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="h-10" data-testid="coupon-input" />
            <Button className="bg-primary hover:bg-primary-dark" data-testid="apply-coupon-btn"
              onClick={() => tryApply(coupons.find((c) => c.code === code.trim().toUpperCase()))}>Apply</Button>
          </div>
          <div className="mt-3 space-y-2">
            {coupons.map((c) => (
              <button key={c.code} type="button" onClick={() => tryApply(c)} data-testid={`coupon-${c.code}`}
                className="w-full text-left p-3 border border-dashed border-border rounded-lg hover:border-accent transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{c.code}</p>
                    <p className="text-xs text-muted-foreground">{c.description}</p>
                  </div>
                  <Tag className="w-4 h-4 text-primary" />
                </div>
              </button>
            ))}
          </div>
        </>
      )}
      {applied && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between" data-testid="applied-coupon">
          <div>
            <p className="font-semibold text-green-900">{applied.code}</p>
            <p className="text-sm text-green-700">{applied.description}</p>
          </div>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={onRemove} data-testid="remove-coupon-btn">Remove</Button>
        </div>
      )}
    </div>
  );
};
